import { Selector } from '@ngxs/store';
import { FilterEventsState, FilterEventsStateModel } from './filter-events.state';
import { ActiveEventDetailOutputDto } from '@smatchy/sdk';
import { FilterDateTypeExplore } from './filter-events.actions';
import { GeoLocality } from '../../locations';
import { ExploreGroupsState, ExploreGroupsStateModel } from '@smatchy/domains/groups/state/explore-groups.state';
import { formatLocationFilterDisplayLabel, isProximitySearchLevel } from '../../../utils/resolve-admin-level.util';
import {
  ExploreRadiusNotice,
  theRadiusCoveredMeter,
  theRadiusNotice,
} from '../domains/the-explore-radius-widens-only-when-nobody-chose-it';

export class FilterEventsSelector {
  @Selector([FilterEventsState])
  static loading(state: FilterEventsStateModel): boolean {
    return state.loading;
  }

  @Selector([FilterEventsState])
  static cityWithCountry(state: FilterEventsStateModel): string {
    // `?.` on the locality too, not only on the location: the chain stopped one
    // step short, so a filter with a location and no locality threw here. An
    // empty string is what a filter with no city has to show.
    const locality = state.location?.locality;

    return locality ? `${locality.city}, ${locality.country}` : '';
  }

  @Selector([FilterEventsState])
  static cityWithRadius(state: FilterEventsStateModel): string | void {
    const locality = state.location?.locality;
    if (!locality) {
      return;
    }
    // The radius covered, not the one asked for: when the default widened to
    // 30 km, the header saying 10 km would contradict the 25 km on a card.
    return formatLocationFilterDisplayLabel(locality, theRadiusCoveredMeter(state));
  }

  @Selector([FilterEventsState])
  static isHierarchyLocationSearch(state: FilterEventsStateModel): boolean {
    const level = state.location?.locality?.adminLevel;
    return !!level && !isProximitySearchLevel(level);
  }

  @Selector([FilterEventsState])
  static partnersOnly(state: FilterEventsStateModel): boolean {
    return state.partnersOnly === true;
  }

  @Selector([FilterEventsState])
  static eventsExplore(state: FilterEventsStateModel): ActiveEventDetailOutputDto[] {
    return state.events;
  }

  @Selector([FilterEventsState, ExploreGroupsState])
  static haveAnyActivityOrGroupToPurpose(
    stateEvent: FilterEventsStateModel,
    stateGroup: ExploreGroupsStateModel,
  ): boolean {
    return stateEvent.events.length + stateGroup.groups.length > 0;
  }

  /**
   * Whether the activities row has anything to show. The radar of the empty
   * Explorer (design review 13A) waits for this alone: it used to wait for no
   * activity AND no group, so a search that found groups but no activity drew
   * the activities title over an empty row, and the radar never came
   * (captures of #1103, 28/10/2026).
   */
  @Selector([FilterEventsState])
  static haveAnyActivityToPurpose(state: FilterEventsStateModel): boolean {
    return state.events.length > 0;
  }

  @Selector([FilterEventsState])
  static eventsFound(state: FilterEventsStateModel): ActiveEventDetailOutputDto[] {
    return state.events;
  }

  @Selector([FilterEventsState])
  static countEventsFound(state: FilterEventsStateModel): number {
    return state.events.length;
  }

  @Selector([FilterEventsState])
  static filter(state: FilterEventsStateModel): FilterEventsStateModel {
    return state;
  }

  @Selector([FilterEventsState])
  static radius(state: FilterEventsStateModel): number {
    return state.radius;
  }

  @Selector([FilterEventsState])
  static radiusChosen(state: FilterEventsStateModel): boolean {
    return state.radiusChosen === true;
  }

  /**
   * The radius the last explore search covered, in metres: the widened tier
   * when the search widened, the radius sent otherwise. Undefined for a
   * hierarchy search, and before the first search.
   */
  @Selector([FilterEventsState])
  static searchedRadiusMeter(state: FilterEventsStateModel): number | undefined {
    return state.searchedRadiusMeter;
  }

  /**
   * How far the « Préviens-moi » alert listens after a search: as far as the
   * search went. After a default search widened to 50 km and found nothing,
   * an alert at 10 km would promise less than the search just covered.
   */
  @Selector([FilterEventsState])
  static radiusTheAlertListensTo(state: FilterEventsStateModel): number {
    return theRadiusCoveredMeter(state);
  }

  /**
   * The radius a header names beside the place, in metres: see
   * theRadiusCoveredMeter. Not the radius to search with, which stays
   * `radius`: sending the covered one back would turn a widened default into
   * a choice nobody made.
   */
  @Selector([FilterEventsState])
  static radiusCovered(state: FilterEventsStateModel): number {
    return theRadiusCoveredMeter(state);
  }

  @Selector([FilterEventsState])
  static radiusNotice(state: FilterEventsStateModel): ExploreRadiusNotice | undefined {
    return theRadiusNotice({
      widened: state.widenedRadius === true,
      searchedRadiusMeter: state.searchedRadiusMeter,
      radiusMeter: state.searchedRadiusMeter ?? state.radius,
      chosenByPerson: state.radiusChosen === true,
      resultsCount: state.events.length,
    });
  }

  @Selector([FilterEventsState])
  static location(state: FilterEventsStateModel): FilterEventsStateModel['location'] {
    return state.location;
  }

  @Selector([FilterEventsState])
  static locality(state: FilterEventsStateModel): GeoLocality | undefined {
    return state.location?.locality;
  }

  @Selector([FilterEventsState])
  static allDatesPreSelected(state: FilterEventsStateModel): { label: string; value: FilterDateTypeExplore }[] {
    return state.allDatesPreSelected;
  }

  @Selector([FilterEventsState])
  static filtersDateTypeExplore(state: FilterEventsStateModel): FilterDateTypeExplore[] {
    return state.filtersDateTypeExplore;
  }
}
